import { z } from "zod";
import { timestampSchema } from "./timestamp";
import { roleSchema } from "./user";

export const avatarColors = [
  "rose",
  "amber",
  "emerald",
  "sky",
  "violet",
  "fuchsia",
] as const;

/**
 * Username dziecka — unikalny w obrębie household. Używany jako część
 * fake-email dla Firebase Auth: `<username>@<slug>.kids.sianko.local`.
 * Dziecko nie widzi tego maila — wpisuje tylko username + slug rodziny.
 */
export const memberUsernameSchema = z
  .string()
  .min(3)
  .max(20)
  .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, "Tylko a-z, 0-9 i myślnik");

export const memberSchema = z.object({
  userId: z.string(),
  displayName: z.string().min(1).max(40),
  /**
   * Etykieta relacji widoczna w UI zamiast surowego role.
   * Np. "Mama", "Tata", "Babcia", "Dziadek", "Wujek".
   * Gdy null/undefined — UI pokazuje fallback („Rodzic"/„Dziecko").
   * Sama rola wciąż decyduje o uprawnieniach (parent vs child).
   */
  relation: z.string().min(1).max(20).optional(),
  /**
   * Username (lowercase, a-z 0-9 -). Tylko dla kont dziecka utworzonych
   * przez username-flow w Settings. Stare konta (email-only) mają
   * undefined. UI: jeśli ustawione, dziecko może logować się przez
   * username+slug zamiast emaila.
   */
  username: memberUsernameSchema.optional(),
  role: roleSchema,
  avatarColor: z.enum(avatarColors),
  balanceMinor: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  /**
   * Tarcza streaka — lista uratowanych dni (północ lokalna).
   * Algorytm streaka traktuje te daty jakby były wykonane.
   * Tarcza odnawia się raz w tygodniu (poniedziałek): dostępna gdy
   * najnowszy rescue jest sprzed startOfThisWeek.
   */
  streakShieldRescues: z.array(timestampSchema).optional(),
  /**
   * Admin household — założyciel rodziny (lub osoba przez niego promowana).
   * Tylko admin może: tworzyć/usuwać domowników, zmieniać slug rodziny,
   * zmieniać hasło/username innych. Backfill: dla legacy household bez
   * tej flagi Cloud Function `selfHealMember` ustawia isAdmin=true gdy
   * caller jest createdBy.
   */
  isAdmin: z.boolean().optional(),
  /**
   * Rok urodzenia dziecka. Zapisywany jako rok (a nie pełna data) —
   * dla preset wiekowych granularność miesięcy/dni nie ma znaczenia,
   * a rok nie starzeje się ekranu jak `age`. Wiek pobieramy przez
   * `ageFromBirthYear(birthYear)` w `src/lib/utils/dates.ts`.
   * Optional — legacy members (sprzed wprowadzenia pola) i konta rodziców
   * pozostają bez tego pola.
   */
  birthYear: z.number().int().min(2005).max(2030).optional(),
  /**
   * Ostatni „heartbeat" — timestamp z momentu wejścia do aplikacji
   * (throttled klient-side: 1x na 5 min per uid). Pisze sam user (rules:
   * self-update). Używane wyłącznie do panelu admin do liczenia DAU/WAU/MAU
   * i odróżnienia „aktywny ale milczy" od „nigdy nie wraca".
   * Optional — legacy members + każdy kto jeszcze nie zalogował się po
   * wprowadzeniu pola pozostają bez wartości (= „nigdy lub bardzo dawno").
   */
  lastActiveAt: timestampSchema.optional(),
});
export type Member = z.infer<typeof memberSchema>;
